// Shared visual building blocks for the per-type modify flow. Extracted from
// ModifyTypeModal so the employee /benefits wizard can present the same
// numbered sections, dependent-row UX, and waive-reason picker.
//
// These are pure presentational components — they own no fetching, no global
// state. Each surface owns the underlying value/onChange and adapts it to the
// shape these components expect.

import { AlertTriangle, Info } from "lucide-react";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import { WAIVE_REASONS, getWaiveReasonLabel } from "@shared/utils/waiveReasons";
import { determineCoverageTierFromDependents } from "@/lib/enrollmentFilters";
import { calculateAge } from "@shared/lib/ageDetermination";

function initialsOf(first?: string | null, last?: string | null) {
  const f = (first || "").trim();
  const l = (last || "").trim();
  return ((f[0] || "") + (l[0] || "")).toUpperCase() || "?";
}

// ─────────────────────────────────────────────────────────────────────────────
// SectionHeader — numbered step badge (01 · 02 · 03 …) + title
// ─────────────────────────────────────────────────────────────────────────────

export function SectionHeader({ index, title }: { index: number; title: string }) {
  return (
    <div className="flex items-center gap-2 text-[11px] uppercase tracking-wide font-semibold text-stone-500 dark:text-stone-400">
      <span className="inline-flex h-5 min-w-[20px] px-1 items-center justify-center rounded-full bg-violet-600 text-white text-[10px] font-bold">
        {String(index).padStart(2, "0")}
      </span>
      <span>{title}</span>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// WaiveReasonPicker — amber pill + dropdown + optional "describe" textarea
// ─────────────────────────────────────────────────────────────────────────────

export function WaiveReasonPicker({
  reason,
  onReason,
  otherText,
  onOtherText,
}: {
  reason: string;
  onReason: (v: string) => void;
  otherText: string;
  onOtherText: (v: string) => void;
}) {
  return (
    <div className="rounded-md border border-stone-200 dark:border-stone-700 bg-stone-50 dark:bg-stone-900/50 p-3 space-y-3">
      <div className="rounded-md border border-amber-300 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-800/60 px-3 py-2 flex items-start gap-2 text-xs text-amber-900 dark:text-amber-100">
        <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
        The employee can re-enroll during open enrollment or a qualifying life event.
      </div>
      <div>
        <Label className="text-[11px] uppercase font-semibold tracking-wide text-stone-500">
          Reason for waiving <span className="text-amber-700">*</span>
        </Label>
        <Select value={reason} onValueChange={onReason}>
          <SelectTrigger className="mt-1">
            <SelectValue placeholder="Select reason" />
          </SelectTrigger>
          <SelectContent>
            {WAIVE_REASONS.map((r) => (
              <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      {reason === "other" && (
        <div>
          <Label className="text-[11px]">Describe</Label>
          <Textarea
            value={otherText}
            onChange={(e) => onOtherText(e.target.value)}
            placeholder="Tell us why this coverage is being waived…"
            rows={3}
          />
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// DependentsBody — per-dependent Enroll/Waive button pair, nested waive reason
// ─────────────────────────────────────────────────────────────────────────────

export function DependentsBody({
  dependents,
  depDecisions,
  onSetDecision,
  depWaiveReasons,
  onSetDepWaiveReason,
  depWaiveOtherTexts,
  onSetDepWaiveOtherText,
}: {
  dependents: any[];
  depDecisions: Record<number, "enroll" | "waive">;
  onSetDecision: (id: number, decision: "enroll" | "waive") => void;
  depWaiveReasons: Record<number, string>;
  onSetDepWaiveReason: (id: number, v: string) => void;
  depWaiveOtherTexts: Record<number, string>;
  onSetDepWaiveOtherText: (id: number, v: string) => void;
}) {
  // Spouse first, then children/others; within a group youngest-first
  // (mirrors components/dependents/DependentsList.tsx).
  const relRank = (r?: string | null) => {
    const v = (r || "").toLowerCase();
    return v === "spouse" ? 0 : v === "domestic_partner" ? 1 : 2;
  };
  const sortedDeps = [...(dependents || [])].sort((a: any, b: any) => {
    const ra = relRank(a.relationship), rb = relRank(b.relationship);
    if (ra !== rb) return ra - rb;
    const ta = a.dateOfBirth ? new Date(a.dateOfBirth).getTime() : null;
    const tb = b.dateOfBirth ? new Date(b.dateOfBirth).getTime() : null;
    if (ta != null && tb != null) return tb - ta;
    if (ta != null) return -1;
    if (tb != null) return 1;
    return 0;
  });

  const enrolledDeps = (dependents || []).filter((d: any) => depDecisions[d.id] === "enroll");
  // Canonical classifier — same logic as the card + server, so the tier shown
  // here can never disagree with them for the same selection.
  const tier = determineCoverageTierFromDependents(enrolledDeps, enrolledDeps.map((d: any) => d.id));
  let tierLabel = "Employee only";
  if (tier === "family") tierLabel = "Employee + Family";
  else if (tier === "employee_spouse") tierLabel = "Employee + Spouse";
  else if (tier === "employee_children") tierLabel = "Employee + Children";
  else if (enrolledDeps.length > 0)
    tierLabel = `Employee + ${enrolledDeps.length} dependent${enrolledDeps.length === 1 ? "" : "s"}`;

  return (
    <div className="space-y-2">
      <div className="text-[11.5px] text-stone-500">
        Adding or removing a dependent may change the coverage tier and premium.
      </div>
      <div className="space-y-2">
        {sortedDeps.length === 0 && (
          <div className="text-[11.5px] text-stone-500 italic px-3 py-2">
            No dependents on file.
          </div>
        )}
        {sortedDeps.map((d: any) => {
          const decision = depDecisions[d.id];
          const isEnrolled = decision === "enroll";
          const isWaived = decision === "waive";
          const depReason = depWaiveReasons[d.id] ?? "";
          const depOther = depWaiveOtherTexts[d.id] ?? "";
          const inlineReasonLabel = depReason === "other"
            ? (depOther.trim() || "Other reason")
            : depReason ? getWaiveReasonLabel(depReason) : "";
          return (
            <div key={d.id} className="space-y-2">
              <div
                className={cn(
                  "flex items-center gap-3 px-3 py-2.5 rounded-md border dark:border-stone-700",
                  "border-l-4",
                  isEnrolled && "border-l-violet-500 bg-white dark:bg-stone-900 border-stone-200 dark:border-stone-700",
                  isWaived && "border-l-stone-400 bg-stone-50 dark:bg-stone-900/60 border-stone-200",
                  !isEnrolled && !isWaived && "border-l-transparent bg-white dark:bg-stone-900 border-stone-200",
                )}
              >
                <Avatar className="h-7 w-7">
                  <AvatarFallback className={cn(
                    "text-[10px] font-semibold",
                    isEnrolled && "bg-violet-100 text-violet-700",
                    isWaived && "bg-stone-200 text-stone-500",
                    !isEnrolled && !isWaived && "bg-stone-400 text-white",
                  )}>
                    {initialsOf(d.firstName, d.lastName)}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <div className={cn(
                    "text-sm font-medium",
                    isWaived ? "line-through text-stone-500" : "text-stone-900 dark:text-stone-100",
                  )}>
                    {d.firstName} {d.lastName}
                  </div>
                  <div className="text-[11px] text-stone-500">
                    {d.relationship}
                    {d.dateOfBirth ? ` · Age ${calculateAge(d.dateOfBirth, new Date())}` : ""}
                    {!d.isEligible && " · not eligible"}
                    {isWaived && inlineReasonLabel && (
                      <span className="text-stone-500"> · {inlineReasonLabel}</span>
                    )}
                  </div>
                </div>
                <div className="inline-flex rounded-md border border-stone-200 dark:border-stone-700 overflow-hidden text-[11px] shrink-0">
                  <button
                    type="button"
                    disabled={!d.isEligible}
                    onClick={() => onSetDecision(d.id, "enroll")}
                    className={cn(
                      "px-2.5 py-1 font-semibold transition-colors",
                      isEnrolled
                        ? "bg-violet-600 text-white"
                        : "bg-white dark:bg-stone-900 text-stone-700 dark:text-stone-300 hover:bg-stone-50",
                      !d.isEligible && "opacity-50 cursor-not-allowed",
                    )}
                  >
                    Enroll
                  </button>
                  <button
                    type="button"
                    disabled={!d.isEligible}
                    onClick={() => onSetDecision(d.id, "waive")}
                    className={cn(
                      "px-2.5 py-1 font-semibold border-l border-stone-200 dark:border-stone-700 transition-colors",
                      isWaived
                        ? "bg-stone-700 text-white"
                        : "bg-white dark:bg-stone-900 text-stone-700 dark:text-stone-300 hover:bg-stone-50",
                      !d.isEligible && "opacity-50 cursor-not-allowed",
                    )}
                  >
                    Waive
                  </button>
                </div>
              </div>
              {isWaived && (
                <div className="ml-10 rounded-md border border-stone-200 dark:border-stone-700 bg-stone-50/60 dark:bg-stone-900/40 p-2.5 space-y-2">
                  <Label className="text-[10.5px] uppercase font-semibold tracking-wide text-stone-500">
                    Reason for waiving <span className="text-amber-700">*</span>
                  </Label>
                  <Select value={depReason} onValueChange={(v) => onSetDepWaiveReason(d.id, v)}>
                    <SelectTrigger className="h-8 text-xs">
                      <SelectValue placeholder="Select reason" />
                    </SelectTrigger>
                    <SelectContent>
                      {WAIVE_REASONS.map((r) => (
                        <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {depReason === "other" && (
                    <Textarea
                      value={depOther}
                      onChange={(e) => onSetDepWaiveOtherText(d.id, e.target.value)}
                      placeholder="Describe…"
                      rows={2}
                      className="text-xs"
                    />
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
      <div className="rounded-md border border-blue-200 bg-blue-50 dark:bg-blue-950/30 dark:border-blue-800/60 px-3 py-2 flex items-center gap-2 text-xs text-blue-900 dark:text-blue-100">
        <Info className="h-3.5 w-3.5" />
        Coverage tier will be <b className="font-semibold">{tierLabel}</b>.
      </div>
    </div>
  );
}
